// From 21st.dev: "Presence Avatars" by ddoemonn (https://21st.dev/ddoemonn/presence-avatars), recoloured to Roomate tokens.

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";

const SLOT = {
  type: "spring",
  stiffness: 520,
  damping: 34,
  mass: 0.45,
} as const;
const FADE = { duration: 0.24, ease: [0.23, 1, 0.32, 1] } as const;
const INSTANT = { duration: 0 } as const;

export type PresencePerson = {
  id: string;
  name: string;
  src?: string;
  /** Live connection signal (the same one behind the header's dot). Undefined = don't render a dot. */
  online?: boolean;
};

export type UsePresenceOptions = {
  people: PresencePerson[];
  max?: number;
  announceAfter?: number;
};

export type UsePresenceResult = {
  ordered: PresencePerson[];
  visible: PresencePerson[];
  hidden: PresencePerson[];
  overflow: number;
  total: number;
  summary: string;
  announcement: string;
};

function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = Array.from(words[0]!)[0] ?? "";
  const last =
    words.length > 1 ? (Array.from(words[words.length - 1]!)[0] ?? "") : "";
  return (first + last).toUpperCase();
}

function describe(names: string[]): string {
  if (names.length === 0) return "Nobody here";
  if (names.length === 1) return `${names[0]} is here`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are here`;
  const rest = names.length - 2;
  return `${names[0]}, ${names[1]} and ${rest} ${rest === 1 ? "other" : "others"} are here`;
}

export function usePresence({
  people,
  max = 5,
  announceAfter = 900,
}: UsePresenceOptions): UsePresenceResult {
  const seen = useRef(new Map<string, number>());
  const next = useRef(0);

  const ordered = useMemo(() => {
    const order = seen.current;
    for (const person of people) {
      if (!order.has(person.id)) {
        order.set(person.id, next.current);
        next.current += 1;
      }
    }
    return people
      .slice()
      .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  }, [people]);

  const slots = Math.max(1, max);
  const visible = ordered.slice(0, slots);
  const hidden = ordered.slice(slots);
  const summary = describe(ordered.map((person) => person.name));

  const [announcement, setAnnouncement] = useState(summary);

  useEffect(() => {
    const timer = setTimeout(() => setAnnouncement(summary), announceAfter);
    return () => clearTimeout(timer);
  }, [summary, announceAfter]);

  return {
    ordered,
    visible,
    hidden,
    overflow: hidden.length,
    total: ordered.length,
    summary,
    announcement,
  };
}

const TILE =
  "absolute left-0 top-0 select-none rounded-full bg-paper p-[2px]";
const WELL =
  "relative grid size-full place-items-center overflow-hidden rounded-full bg-paper-2 font-medium leading-none text-ink-2";

type TileProps = {
  person: PresencePerson;
  index: number;
  step: number;
  size: number;
  zIndex: number;
  reduced: boolean;
  vertical: boolean;
  /** When set, the tile is a button that reports itself (used by the rail's person card). */
  onSelect?: (person: PresencePerson, trigger: HTMLButtonElement) => void;
  expanded?: boolean;
};

function PresenceTile({
  person,
  index,
  step,
  size,
  zIndex,
  reduced,
  vertical,
  onSelect,
  expanded = false,
}: TileProps) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const offset = index * step;
  const axis = vertical ? "y" : "x";

  const motionProps = {
    initial: { opacity: 0, scale: 0.86, [axis]: offset },
    animate: { opacity: 1, scale: 1, [axis]: offset },
    exit: { opacity: 0, scale: 0.86 },
    transition: reduced ? INSTANT : SLOT,
    style: {
      width: size,
      height: size,
      zIndex,
      fontSize: Math.round(size * 0.34),
    },
  };
  const inner = (
    <>
      <span className={WELL}>
        {initials(person.name)}

        {person.src && !failed ? (
          <motion.img
            src={person.src}
            alt=""
            width={size}
            height={size}
            decoding="async"
            onLoad={() => setLoaded(true)}
            onError={() => setFailed(true)}
            initial={false}
            animate={{ opacity: loaded ? 1 : 0 }}
            transition={reduced ? INSTANT : FADE}
            className="absolute inset-0 size-full object-cover"
          />
        ) : null}
      </span>

      {person.online !== undefined && (
        <span
          data-testid="presence-dot"
          data-online={person.online ? "true" : "false"}
          aria-hidden
          className={`absolute -bottom-0.5 -right-0.5 size-2 rounded-full ring-2 ring-paper ${person.online ? "bg-ok" : "bg-ink-3"}`}
        />
      )}
    </>
  );

  if (onSelect) {
    return (
      <motion.button
        type="button"
        aria-label={person.name}
        title={person.name}
        aria-haspopup="dialog"
        aria-expanded={expanded}
        data-testid="presence-avatar"
        data-person-id={person.id}
        onClick={(event) => onSelect(person, event.currentTarget)}
        {...motionProps}
        className={`${TILE} cursor-pointer outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-[color:var(--focus)] ${expanded ? "ring-2 ring-[color:var(--focus)]" : ""}`}
      >
        {inner}
      </motion.button>
    );
  }

  return (
    <motion.span aria-hidden title={person.name} {...motionProps} className={TILE}>
      {inner}
    </motion.span>
  );
}

export type PresenceAvatarsProps = {
  people: PresencePerson[];
  max?: number;
  size?: number;
  overlap?: number;
  /** Space between avatars; when set, it replaces `overlap` (a gap of 8 is an overlap of -8). */
  gap?: number;
  label?: string;
  announceAfter?: number;
  onOverflowSelect?: (hidden: PresencePerson[]) => void;
  /** Makes every visible avatar a button; receives the button so the caller can anchor a popover to it. */
  onSelect?: (person: PresencePerson, trigger: HTMLButtonElement) => void;
  /** The person whose popover is open, so its avatar reads as expanded. */
  selectedId?: string | null;
  /** Stack top-to-bottom instead of left-to-right. Default horizontal. */
  orientation?: "horizontal" | "vertical";
  className?: string;
};

export function PresenceAvatars({
  people,
  max = 5,
  size = 28,
  overlap: overlapProp = 9,
  gap,
  label = "People here",
  announceAfter,
  onOverflowSelect,
  onSelect,
  selectedId = null,
  orientation = "horizontal",
  className = "",
}: PresenceAvatarsProps) {
  const reduced = useReducedMotion();
  const { ordered, visible, hidden, overflow, announcement } = usePresence({
    people,
    max,
    announceAfter,
  });

  const vertical = orientation === "vertical";
  const slots = Math.max(1, max);
  const overlap = gap === undefined ? overlapProp : -gap;
  const step = size - overlap;
  const chip = size + 8;

  const rail =
    visible.length === 0
      ? 0
      : overflow > 0
        ? visible.length * step + chip
        : (visible.length - 1) * step + size;

  const chipCount = `+${Math.min(overflow, 99)}`;
  const axis = vertical ? "y" : "x";
  const chipMotion = {
    initial: { opacity: 0, scale: 0.86 },
    animate: { opacity: 1, scale: 1, [axis]: visible.length * step },
    exit: { opacity: 0, scale: 0.86 },
    transition: reduced ? INSTANT : SLOT,
  };
  const chipClass =
    "absolute left-0 top-0 grid place-items-center rounded-full border border-rule bg-card font-mono text-[10.5px] leading-none tabular-nums text-ink-3 outline-none ring-2 ring-paper";

  return (
    <div
      role="group"
      aria-label={label}
      className={`inline-flex items-center ${vertical ? "flex-col" : ""} ${className}`}
    >
      <motion.div
        className="relative shrink-0"
        style={vertical ? { width: size } : { height: size }}
        initial={false}
        animate={vertical ? { height: rail } : { width: rail }}
        transition={reduced ? INSTANT : SLOT}
      >
        <AnimatePresence initial={false}>
          {visible.map((person, i) => (
            <PresenceTile
              key={person.id}
              person={person}
              index={i}
              step={step}
              size={size}
              zIndex={slots - i}
              reduced={Boolean(reduced)}
              vertical={vertical}
              onSelect={onSelect}
              expanded={selectedId === person.id}
            />
          ))}

          {overflow > 0 &&
            (onOverflowSelect ? (
              <motion.button
                key="overflow"
                type="button"
                onClick={() => onOverflowSelect(hidden)}
                aria-label={`Show ${overflow} more`}
                style={{ width: vertical ? size : chip, height: vertical ? chip : size, zIndex: 0 }}
                className={`${chipClass} focus-visible:border-[color:var(--focus)]`}
                {...chipMotion}
              >
                <span aria-hidden>{chipCount}</span>
              </motion.button>
            ) : (
              <motion.span
                key="overflow"
                aria-hidden
                style={{ width: vertical ? size : chip, height: vertical ? chip : size, zIndex: 0 }}
                className={chipClass}
                {...chipMotion}
              >
                {chipCount}
              </motion.span>
            ))}
        </AnimatePresence>
      </motion.div>
      {/* The roster names everyone, including people past the visible slots that have no button. */}
      <ul className="sr-only">
        {ordered.map((person) => (
          // A visible person is already announced by their named button; only the rest rely on this roster.
          <li key={person.id} aria-hidden={onSelect && visible.some((v) => v.id === person.id) ? true : undefined}>
            {person.name}
          </li>
        ))}
      </ul>
      <span
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {announcement}
      </span>
    </div>
  );
}

export default PresenceAvatars;


